import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOpportunityWizard } from './OpportunityWizardContext';
import OpportunityWizardLayout from './OpportunityWizardLayout';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, Download, Copy, Check, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Step7Proposal = () => {
  const navigate = useNavigate();
  const { state, resetWizard } = useOpportunityWizard();
  const [isGenerating, setIsGenerating] = useState(false);
  const [proposalGenerated, setProposalGenerated] = useState(false);
  const [proposalUrl, setProposalUrl] = useState('');
  const [proposalType, setProposalType] = useState('standard');
  const [linkCopied, setLinkCopied] = useState(false);

  // Verificar se temos as informações necessárias
  if (!state.selectedCustomer) {
    navigate('../step1', { relative: 'path' });
    return null;
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: state.currencyCode,
      minimumFractionDigits: 2
    }).format(value);
  };

  const getTotalValue = () => {
    return state.selectedPlans.reduce((total, plan) => {
      return total + (plan.quantity * plan.unit_price);
    }, 0);
  };

  const handleGenerateProposal = async () => {
    if (state.selectedPlans.length === 0) {
      toast.error('Adicione pelo menos um produto para gerar a proposta');
      return;
    }

    setIsGenerating(true);

    try {
      // Simular o tempo de geração da proposta
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Aqui seria a lógica para gerar a proposta usando serviços externos
      // ou processamento interno para criar um PDF ou link para a proposta
      
      // Simulando URL da proposta gerada
      const mockProposalUrl = `https://example.com/proposals/${state.selectedCustomer.id}-${Date.now()}.pdf`;
      setProposalUrl(mockProposalUrl);
      setProposalGenerated(true);
      
      toast.success('Proposta gerada com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar proposta:', error);
      toast.error('Ocorreu um erro ao gerar a proposta');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(proposalUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
    toast.success('Link da proposta copiado!');
  };

  const handleCreateOpportunity = async () => {
    if (state.selectedPlans.length === 0) {
      toast.error('Adicione pelo menos um produto para continuar');
      return;
    }

    setIsGenerating(true);

    try {
      // Criar a oportunidade
      const { data: opportunity, error: opportunityError } = await supabase
        .from('business_opportunities')
        .insert({
          customer_id: state.selectedCustomer.id,
          name: `Oportunidade - ${state.selectedCustomer.name} - ${new Date().toISOString().split('T')[0]}`,
          description: state.opportunityType || `Oportunidade para ${state.selectedCustomer.name}`,
          total_value: getTotalValue(),
          currency_code: state.currencyCode,
          status: 'open',
          probability: state.closeProbability,
          expected_close_date: state.startDate,
          contact_id: state.selectedContacts.length > 0 ? state.selectedContacts[0].contact_id : null
        })
        .select()
        .single();

      if (opportunityError) throw opportunityError;

      // Adicionar produtos à oportunidade
      if (state.selectedPlans.length > 0) {
        const productsData = state.selectedPlans.map(plan => ({
          opportunity_id: opportunity.id,
          subscription_plan_id: plan.subscription_plan_id,
          quantity: plan.quantity,
          unit_price: plan.unit_price,
          discount_percentage: 0,
          total_price: plan.quantity * plan.unit_price
        }));

        const { error: productsError } = await supabase
          .from('opportunity_subscription_plans')
          .insert(productsData);

        if (productsError) throw productsError;
      }

      toast.success('Oportunidade criada com sucesso!');
      
      // Resetar o estado do wizard
      resetWizard();
      
      // Navegar para a página de confirmação
      navigate(`../confirmation/${opportunity.id}`, { relative: 'path' });
    } catch (error) {
      console.error('Error creating opportunity:', error);
      toast.error('Erro ao criar oportunidade');
      setIsGenerating(false);
    }
  };

  return (
    <OpportunityWizardLayout
      currentStep={7}
      totalSteps={7}
      title="Gerar Proposta"
      description="Gere uma proposta comercial para o cliente"
      onPrevious={() => navigate('../step6', { relative: 'path' })}
      nextButtonText="Finalizar"
      onNext={handleCreateOpportunity}
      isNextDisabled={isGenerating}
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-extrabold">Cliente: {state.selectedCustomer?.name}</h2>
            <p className="text-sm text-muted-foreground">
              Total da proposta: {formatCurrency(getTotalValue())}
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Configurações da Proposta</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs defaultValue="standard" value={proposalType} onValueChange={setProposalType}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="standard">Padrão</TabsTrigger>
                <TabsTrigger value="detailed">Detalhada</TabsTrigger>
                <TabsTrigger value="custom">Personalizada</TabsTrigger>
              </TabsList>
              <TabsContent value="standard" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="validity" className="text-right">
                      Validade
                    </Label>
                    <Select defaultValue="30">
                      <SelectTrigger className="col-span-3">
                        <SelectValue placeholder="Selecione a validade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 dias</SelectItem>
                        <SelectItem value="30">30 dias</SelectItem>
                        <SelectItem value="60">60 dias</SelectItem>
                        <SelectItem value="90">90 dias</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="language" className="text-right">
                      Idioma
                    </Label>
                    <Select defaultValue="pt-BR">
                      <SelectTrigger className="col-span-3">
                        <SelectValue placeholder="Selecione o idioma" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                        <SelectItem value="en-US">English (US)</SelectItem>
                        <SelectItem value="es">Español</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="detailed" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="include-pricing" className="text-right">
                      Incluir preços detalhados
                    </Label>
                    <Select defaultValue="yes">
                      <SelectTrigger className="col-span-3">
                        <SelectValue placeholder="Incluir preços detalhados?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Sim</SelectItem>
                        <SelectItem value="no">Não</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="include-terms" className="text-right">
                      Incluir termos e condições
                    </Label>
                    <Select defaultValue="yes">
                      <SelectTrigger className="col-span-3">
                        <SelectValue placeholder="Incluir termos e condições?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Sim</SelectItem>
                        <SelectItem value="no">Não</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="custom" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="template" className="text-right">
                      Template
                    </Label>
                    <Select defaultValue="premium">
                      <SelectTrigger className="col-span-3">
                        <SelectValue placeholder="Selecione o template" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">Básico</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="custom-message" className="text-right">
                      Mensagem personalizada
                    </Label>
                    <Input 
                      id="custom-message"
                      placeholder="Digite uma mensagem personalizada para o cliente"
                      className="col-span-3"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {!proposalGenerated ? (
          <div className="flex justify-center">
            <Button 
              onClick={handleGenerateProposal} 
              disabled={isGenerating}
              size="lg"
              className="min-w-[200px]"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gerando proposta...
                </>
              ) : (
                'Gerar Proposta'
              )}
            </Button>
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Proposta Gerada</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <p className="font-extrabold">Proposta_{state.selectedCustomer?.name.replace(/\s+/g, '_')}.pdf</p>
                  <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString('pt-BR')}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopyLink}>
                    {linkCopied ? (
                      <Check className="h-4 w-4 mr-2" />
                    ) : (
                      <Copy className="h-4 w-4 mr-2" />
                    )}
                    Copiar Link
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Visualizar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </OpportunityWizardLayout>
  );
};

export default Step7Proposal; 